'use client';

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { type CalculatorPricing, type CalculatorSettings } from './calculator-config';

const FIELDS: { key: keyof CalculatorPricing; label: string; coefficient?: boolean }[] = [
  { key: 'steelPerSqm', label: 'Металлокаркас, ₽/м² основания' },
  { key: 'wallWarmPerSqm', label: 'Сэндвич-панели, ₽/м²' },
  { key: 'wallColdPerSqm', label: 'Профлист, ₽/м²' },
  { key: 'windowPrice', label: 'Окно, ₽/шт.' },
  { key: 'gatePrice', label: 'Ворота, ₽/шт.' },
  { key: 'snowCoeff', label: 'Надбавка за снеговой район', coefficient: true },
  { key: 'windCoeff', label: 'Надбавка за ветровой район', coefficient: true },
  { key: 'craneCoeff', label: 'Надбавка за кран-балку', coefficient: true },
];

export default function CalculatorSettingsDialog({ open, onOpenChange, settings, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void;
  settings: CalculatorSettings; onSaved: (settings: CalculatorSettings) => void;
}) {
  const [draft, setDraft] = useState(settings.pricing);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setDraft(settings.pricing); setError(''); } }, [open, settings]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault(); setSaving(true); setError('');
    try {
      const response = await fetch('/api/calculator/settings/', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pricing: draft, revision: settings.revision }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Не удалось сохранить цены.');
      onSaved(data); onOpenChange(false);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Ошибка сохранения.'); }
    finally { setSaving(false); }
  };

  return <Dialog open={open} onOpenChange={(value) => { if (!saving) onOpenChange(value); }}>
    <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Настройки калькулятора</DialogTitle>
        <DialogDescription>После сохранения эти цены применяются к расчётам всех клиентов и менеджеров.</DialogDescription>
      </DialogHeader>
      <form onSubmit={save} className="space-y-4">
        {FIELDS.map(({ key, label, coefficient }) => <div key={key}>
          <label htmlFor={`price-${key}`} className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
          <input id={`price-${key}`} type="number" required min={0} max={coefficient ? 5 : 100000000}
            step={coefficient ? 0.01 : 1} value={draft[key]} disabled={saving}
            onChange={(event) => setDraft((previous) => ({ ...previous, [key]: event.target.valueAsNumber }))}
            className="h-11 w-full rounded-xl border border-slate-200 px-3 text-base" />
        </div>)}
        <p className="text-xs text-slate-500">Коэффициент 0,05 означает надбавку 5%.</p>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <button type="submit" disabled={saving} className="w-full rounded-xl bg-[#214688] px-4 py-3 font-semibold text-white disabled:opacity-50">
          {saving ? 'Сохраняем…' : 'Сохранить цены для всех'}
        </button>
      </form>
    </DialogContent>
  </Dialog>;
}
